import React from 'react';
import { Phone, FileText, Calendar, BarChart2 } from 'lucide-react';
import { APP_NAME } from '../config/branding';

interface StackLayerProps {
    label?: string;
    icon?: React.ReactNode;
    color: string;
    sideColor: string;
    textColor?: string;
    zOffset: number;
    isTop?: boolean;
    logo?: string;
}

const StackLayer: React.FC<StackLayerProps> = ({
    label,
    icon,
    color,
    sideColor,
    textColor = 'text-gray-700',
    zOffset,
    isTop,
    logo
}) => {
    // Increase thickness to fit text (was 16/24)
    const thickness = isTop ? 40 : 35;
    const slices = Array.from({ length: thickness });

    return (
        <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
                transform: `translateZ(${zOffset}px)`,
                transformStyle: 'preserve-3d'
            }}
        >
            {/* Drop Shadow (only for bottom layer effectively, but adding to all doesn't hurt in this context as they stack) */}
            <div
                className="absolute top-0 left-0 w-full h-full rounded-3xl bg-black/20 blur-xl"
                style={{
                    transform: 'translateZ(-40px) translateY(20px) translateX(20px)',
                    opacity: 0.3
                }}
            />

            {/* Extrusion Slices (Thickness) */}
            {slices.map((_, i) => (
                <div
                    key={i}
                    className={`absolute top-0 left-0 w-full h-full rounded-3xl ${sideColor}`}
                    style={{
                        transform: `translateZ(-${i + 1}px)`,
                    }}
                />
            ))}

            {/* Top Face with Inner Lip */}
            <div
                className={`absolute top-0 left-0 w-full h-full rounded-3xl ${color} flex items-center justify-center`}
                style={{
                    transform: 'translateZ(0px)',
                    boxShadow: 'inset 0 0 0 4px rgba(255,255,255,0.1)' // Inner lip effect
                }}
            >
                {/* Inner Darker Area for "Tray" effect on non-top layers */}
                {!isTop && (
                    <>
                        {/* Dark Back Edges (simulating depth inside the tray) - Positioned at the back corner */}
                        <div
                            className="absolute rounded-2xl bg-gray-900"
                            style={{
                                top: '8px',
                                left: '8px',
                                width: '60%',
                                height: '60%',
                                transform: 'translateZ(-2px)',
                                opacity: 0.8
                            }}
                        />

                        {/* Light Blue Inner Accent - Matches top layer blue */}
                        <div className="absolute inset-2 rounded-2xl bg-blue-600 shadow-inner border border-blue-400/30" />
                    </>
                )}

                {isTop && (
                    <img
                        src="/logo_clear_back.png"
                        alt={`${APP_NAME} Logo`}
                        className="w-48 h-48 object-contain relative z-10"
                    />
                )}
            </div>

            {/* Left Side Label (Visible Face 1) */}
            {!isTop && label && (
                <div
                    className="absolute w-full flex items-center justify-center"
                    style={{
                        bottom: '-29px',
                        left: '15px',
                        height: `${thickness}px`,
                        transformOrigin: 'bottom',
                        transform: 'rotateX(-90deg)', // Fold down from bottom edge
                    }}
                >
                    <div
                        className={`w-full h-full flex items-center justify-center px-6 ${textColor} font-bold text-xl tracking-wide`}
                    >
                        {label}
                    </div>
                </div>
            )}

            {/* Right Side Icon (Visible Face 2) */}
            {!isTop && icon && (
                <div
                    className="absolute h-full flex items-center justify-center"
                    style={{
                        right: '-29px', // Adjusted to center icon (was -40px which was too low)
                        top: '15px',
                        width: `${thickness}px`,
                        transformOrigin: 'right',
                        transform: 'rotateY(90deg)', // Fold right from right edge
                    }}
                >
                    <div
                        className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-gray-50"
                        style={{
                            transform: 'rotate(-90deg) translateZ(10px)', // Pop out effect
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), inset 0 2px 4px rgba(255,255,255,0.5)'
                        }}
                    >
                        {icon}
                    </div>
                </div>
            )}
        </div>
    );
};

const IsometricTechStack = () => {
    const layers = [
        {
            label: "Client Notifications",
            icon: <BarChart2 className="w-7 h-7 text-orange-500" />,
            color: "bg-white dark:bg-dark-bg-tertiary",
            sideColor: "bg-gray-200 dark:bg-gray-700",
            textColor: "text-gray-900 dark:text-dark-text-primary",
            zOffset: 0
        },
        {
            label: "Scheduling",
            icon: <Calendar className="w-7 h-7 text-blue-500" />,
            color: "bg-white dark:bg-dark-bg-tertiary",
            sideColor: "bg-gray-200 dark:bg-gray-700",
            textColor: "text-gray-900 dark:text-dark-text-primary",
            zOffset: 70 // Increased spacing
        },
        {
            label: "Medical Records",
            icon: <FileText className="w-7 h-7 text-purple-500" />,
            color: "bg-white dark:bg-dark-bg-tertiary",
            sideColor: "bg-gray-200 dark:bg-gray-700",
            textColor: "text-gray-900 dark:text-dark-text-primary",
            zOffset: 140 // Increased spacing
        },
        {
            label: "AI Front Desk",
            icon: <Phone className="w-7 h-7 text-green-500" />,
            color: "bg-white dark:bg-dark-bg-tertiary",
            sideColor: "bg-gray-200 dark:bg-gray-700",
            textColor: "text-gray-900 dark:text-dark-text-primary",
            zOffset: 210 // Increased spacing
        },
        {
            isTop: true,
            color: "bg-blue-600",
            sideColor: "bg-blue-800",
            zOffset: 280 // Increased spacing
        }
    ];

    return (
        <div className="w-full h-full flex items-center justify-center py-20 mt-40 scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={{
                    transformStyle: 'preserve-3d',
                    transform: 'rotateX(55deg) rotateZ(45deg)',
                }}
            >
                {layers.map((layer, index) => (
                    <StackLayer key={index} {...layer} />
                ))}
            </div>
        </div>
    );
};

export default IsometricTechStack;
